/**
 * Saves a finished design before the shopper reaches for "Dodaj u korpu".
 *
 * Add-to-cart used to do all of its work on the click: draw the cart picture,
 * upload it, commit the design — seconds of "Čuvanje…" on a phone, and a click
 * that a single dropped request could fail. Now the same work runs in the
 * background once every required answer is in and the shopper pauses (and
 * again after each change), and the server keeps the result `held` — nothing
 * renders, no song downloads — until it actually goes into a cart. The click
 * then only writes the saved id into WooCommerce's form.
 *
 * This class is the bookkeeping only: which design (by signature) was saved,
 * which one is being saved, and when to start. What "saving" means is the
 * caller's `run`.
 */

export interface CartPrepDesign<T> {
  /** Everything that makes the order what it is; equal signatures, equal orders. */
  signature: string;
  run(): Promise<T>;
}

export interface CartPrepOptions<T> {
  /** The design as it stands, or undefined while it cannot be committed yet. */
  current(): CartPrepDesign<T> | undefined;
  /** The pause after the last change before saving starts. */
  delayMs?: number;
  /** Test seams; the page uses window timers. */
  setTimer?: (run: () => void, ms: number) => unknown;
  clearTimer?: (handle: unknown) => void;
}

export class CartPrep<T> {
  private timer: unknown;
  private saved: { signature: string; result: T } | undefined;
  private running: { signature: string; promise: Promise<T | undefined> } | undefined;
  /**
   * A design whose save failed is not tried again in the background: the
   * click does the work itself and shows the shopper what went wrong.
   */
  private failed: string | undefined;
  /** Bumped by `reset`: a save that finishes afterwards belongs to nothing. */
  private generation = 0;

  constructor(private readonly options: CartPrepOptions<T>) {}

  /** Something changed: save again once the shopper pauses. */
  schedule(delayMs = this.options.delayMs ?? 1200): void {
    this.cancelTimer();
    this.timer = this.setTimer(() => {
      this.timer = undefined;
      void this.start();
    }, delayMs);
  }

  /** The shopper is reaching for the button: save now, if not already saved. */
  now(): void {
    this.cancelTimer();
    void this.start();
  }

  /**
   * The saved result for exactly this design — waiting for a save of it that
   * is already under way — or undefined, and the click does the work itself.
   */
  async take(signature: string): Promise<T | undefined> {
    if (this.saved?.signature === signature) return this.saved.result;
    const running = this.running;
    if (running?.signature !== signature) return undefined;
    const result = await running.promise;
    return this.saved?.signature === signature ? result : undefined;
  }

  /** Whether this design is saved or being saved (for tests and the harness). */
  state(signature: string): 'saved' | 'saving' | 'none' {
    if (this.saved?.signature === signature) return 'saved';
    if (this.running?.signature === signature) return 'saving';
    return 'none';
  }

  /**
   * Drops the saved result if it is this one — it is about to be replaced on
   * the server, and a click must never add a design that no longer exists.
   */
  forget(match: (result: T) => boolean): void;
  forget(id: string): void;
  forget(which: string | ((result: T) => boolean)): void {
    const saved = this.saved;
    if (!saved) return;
    const hit = typeof which === 'function'
      ? which(saved.result)
      : (saved.result as { id?: unknown } | undefined)?.id === which;
    if (hit) this.saved = undefined;
  }

  /** Forgets everything: the saved design went into the cart, or the product changed. */
  reset(): void {
    this.cancelTimer();
    this.saved = undefined;
    this.running = undefined;
    this.failed = undefined;
    this.generation++;
  }

  private start(): Promise<T | undefined> | undefined {
    const design = this.options.current();
    if (!design) return undefined;
    const { signature } = design;
    if (this.saved?.signature === signature) return Promise.resolve(this.saved.result);
    if (this.running?.signature === signature) return this.running.promise;
    if (this.failed === signature) return undefined;
    const generation = this.generation;
    let promise: Promise<T | undefined>;
    const settle = (): boolean => {
      if (this.running?.promise === promise) this.running = undefined;
      return generation === this.generation;
    };
    promise = design.run().then(
      (result) => {
        if (!settle()) return undefined;
        this.saved = { signature, result };
        return result;
      },
      () => {
        if (settle()) this.failed = signature;
        return undefined;
      },
    );
    this.running = { signature, promise };
    return promise;
  }

  private setTimer(run: () => void, ms: number): unknown {
    return this.options.setTimer ? this.options.setTimer(run, ms) : setTimeout(run, ms);
  }

  private cancelTimer(): void {
    if (this.timer === undefined) return;
    if (this.options.clearTimer) this.options.clearTimer(this.timer);
    else clearTimeout(this.timer as ReturnType<typeof setTimeout>);
    this.timer = undefined;
  }
}
